import {Flag} from 'platform/components';
import {Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import type {ReactElement} from 'react';

import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER} from 'shared/web';

import {useAuctionCountdown} from '../../../hooks/useAuctionCountdown';
import {formatAuctionEndDate} from '../utils/formatAuctionEndDate';

interface AuctionCountdownCellProps {
  endTime?: string | null;
  isEnded: boolean;
}

export function AuctionCountdownCell(props: AuctionCountdownCellProps) {
  const countdown = useAuctionCountdown(props.endTime, props.isEnded);
  const dateLabel = countdown.hasEndTime
    ? formatAuctionEndDate(props.endTime ?? '')
    : EMPTY_PLACEHOLDER;
  const countdownContent = match({
    isEnded: countdown.isEnded,
    hasElapsed: countdown.hasElapsed,
    isEndingSoon: countdown.isEndingSoon,
    hasEndTime: countdown.hasEndTime,
  })
    .returnType<ReactElement | null>()
    .with({isEnded: true}, () => (
      <Flag label={i18n.t('entity.auction.status.ended')} colorScheme="neutral" size="small" />
    ))
    // A lot extended by late bids keeps a stale end time in the past. The badge next to the listed
    // time says the lot still runs, so the time stays visible - a tooltip would hide it on touch.
    .with({hasElapsed: true}, () => (
      <Flag label={i18n.t('entity.auction.status.extended')} colorScheme="orange" size="small" />
    ))
    .with({isEndingSoon: true, hasEndTime: true}, () => (
      <Text size="xxSmall" color="warning" noWrap overflowWrap="anywhere">
        {i18n.t('entity.auction.status.endingSoon')}
      </Text>
    ))
    .with({hasEndTime: true}, () => (
      <Text size="xxSmall" color="secondary" noWrap overflowWrap="anywhere">
        {countdown.label}
      </Text>
    ))
    .otherwise(() => null);

  return (
    <VStack spacing={1}>
      <Text
        size="xSmall"
        color={countdown.hasElapsed ? 'secondary' : 'primary'}
        noWrap
        overflowWrap="anywhere"
      >
        {dateLabel}
      </Text>
      {countdownContent}
    </VStack>
  );
}
